import { KPIPicker } from "@/components/kpi/KPIPicker";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField, DateField, CurrencyField } from "@/components/form/Fields";
import { MultiSelect } from "@/components/form/MultiSelect";
import { useOptions, userOptions, quickAddOrg, quickAddContact, propertyOptions } from "@/components/hooks/useOptions";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SIZE_METRICS, SIZE_PERIODS } from "@/lib/constants";
import { useConfig } from "@/components/hooks/useConfig";
import { useAgentCampaignOptions } from "@/components/opportunities/CampaignLinks";
import { usePermissions } from "@/context/PermissionsContext";

export function OpportunityModal({ open, prefill = {}, record, onClose, onSuccess }) {
  const { users } = useAuth();
  const { can } = usePermissions();
  const { orgs, contacts, props } = useOptions(open);
  const cfg = useConfig();
  const campaignOpts = useAgentCampaignOptions();
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState(record || prefill || {});
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    client.get("/pipeline/stages").then(({ data }) => {
      setStages(data.stages);
      setForm((f) => (f.stage_id ? f : { ...f, stage_id: data.stages[0]?.id }));
    });
  }, [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setForm(record || prefill || {}); }, [open, record]);

  const stageOpts = stages.map((s) => ({ value: s.id, label: s.name }));
  const activeStage = stages.find((s) => s.id === form.stage_id);
  const orgOpts = orgs.filter((o) => o.org_type !== "DTR Property").map((o) => ({ value: o.id, label: o.name, search: true }));
  const propOpts = propertyOptions(props);
  const contactOpts = contacts.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name || ""}`.trim(), search: true }));
  const metric = form.size_metric || "";

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    setSaving(true);
    try {
      const isEdit = record && record.id;
      const body = { ...form };
      ["size_value", "probability"].forEach((k) => {
        if (body[k] === "" || body[k] === undefined || body[k] === null) {
          if (isEdit) body[k] = null;
          else delete body[k];
        } else body[k] = Number(body[k]);
      });
      const { data } = isEdit
        ? await client.patch(`/opportunities/${record.id}`, body)
        : await client.post("/opportunities", body);
      toast.success(`Opportunity ${isEdit ? "updated" : "created"}`);
      onSuccess && onSuccess(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="opportunity-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{record ? "Edit Opportunity" : "New Opportunity"}</DialogTitle>
          <DialogDescription className="sr-only">Track a new business deal through the pipeline</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <TextField label="Deal Title" required value={form.title} onChange={set("title")} testid="opp-field-title" placeholder="e.g. 2027 Campaign — Luxury Escapes" />

          <MultiSelect label="Type" value={form.types || []} onChange={set("types")} options={cfg.opportunity_types.map((t) => ({ value: t, label: t }))} testid="opp-field-types" placeholder="Select one or more types…" />

          <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3">
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Size</label>
            <div className="grid grid-cols-3 gap-3">
              {metric === "Revenue" ? <CurrencyField value={form.size_value} onChange={set("size_value")} testid="opp-field-size-value" /> : <TextField type="number" value={form.size_value} onChange={set("size_value")} testid="opp-field-size-value" placeholder="0" />}
              <SelectField value={form.size_metric} onChange={set("size_metric")} options={SIZE_METRICS} placeholder="Metric" testid="opp-field-size-metric" />
              <SelectField value={form.size_period} onChange={set("size_period")} options={SIZE_PERIODS} placeholder="Period" testid="opp-field-size-period" />
            </div>
            {metric && <p className="mt-1.5 text-[11px] text-slate-400">{metric === "Revenue" ? "Revenue in AUD" : `Number of ${metric.toLowerCase()}`}</p>}
          </div>

          <MultiSelect label="Organisations" value={form.organisation_ids || []} onChange={set("organisation_ids")} options={orgOpts} onQuickAdd={quickAddOrg} testid="opp-field-orgs" placeholder="Search organisations to link…" />
          <MultiSelect propertyField label="Property" value={form.property_ids || []} onChange={set("property_ids")} options={propOpts} testid="opp-field-properties" placeholder="Search properties to link…" />
          <MultiSelect label="Contacts" value={form.contact_ids || []} onChange={set("contact_ids")} options={contactOpts} onQuickAdd={quickAddContact} testid="opp-field-contacts" placeholder="Search contacts to link…" />

          <MultiSelect label="Agent Campaigns" value={form.campaign_ids || []} onChange={set("campaign_ids")} options={campaignOpts} testid="opp-field-campaigns" placeholder="Search agent campaigns to link…" />

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Stage" value={form.stage_id} onChange={set("stage_id")} options={stageOpts} allowEmpty={false} testid="opp-field-stage" />
            <SelectField label="Owner" value={form.owner_id} onChange={set("owner_id")} options={userOptions(users)} testid="opp-field-owner" />
          </div>
          {can("kpis", "view") && (
            <KPIPicker value={form.kpi_ids || []} onChange={set("kpi_ids")} userIds={[form.owner_id]} testid="opp-field-kpis" />
          )}
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Probability %" type="number" value={form.probability} onChange={set("probability")} testid="opp-field-probability" placeholder="0" />
            <DateField label="Close Date" value={form.expected_close_date} onChange={set("expected_close_date")} testid="opp-field-close" />
          </div>
          {activeStage?.is_lost && (
            <TextField label="Lost Reason" value={form.lost_reason} onChange={set("lost_reason")} testid="opp-field-lost-reason" placeholder="Why was this deal lost?" />
          )}
          <TextArea label="Notes" value={form.notes} onChange={set("notes")} testid="opp-field-notes" />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="opp-save-button" className="bg-sky-600 hover:bg-sky-700">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
